/**
 * components/landing/pricing/data.js — Phase 7 · single source of truth
 * for the /pricing (Commercial Decision Center) narrative.  All copy
 * lives here so PRD edits happen in one place.
 *
 * PRICES are never hardcoded here — they come from the CENTRALIZED
 * PRICING ENGINE (`/app/lib/pricing.js`).  Monthly WL plan retired
 * Prices live in the centralized pricing engine (lib/pricing.js).
 */
import { WL_PLAN_BY_ID, WL_BENEFITS, fmtUsd } from '@/lib/pricing'

const WL = {
  starter:      WL_PLAN_BY_ID.quarterly,
  professional: WL_PLAN_BY_ID.halfyearly,
  enterprise:   WL_PLAN_BY_ID.annual,
}

// ── Enterprise Licensing Plans ─────────────────────────────────────
export const PLANS = [
  {
    id: 'starter',
    planId: WL.starter.id,
    label: 'Starter License',
    price: fmtUsd(WL.starter.amount_cents),
    currency: 'USD',
    period: WL.starter.period,
    duration: WL.starter.duration,
    highlight: false,
    bestValue: false,
    tagline: 'Launch fast',
    cta: 'Launch Starter',
    bestFor: [
      'Pilot projects',
      'Startup trading businesses',
      'Boutique financial firms',
      'Product validation',
    ],
    includes: [
      'Complete Platform',
      ...WL_BENEFITS.items,
      'White-Label',
      'AI Platform',
      'Trading Platform',
      'Academy',
      'Analytics',
      'Stripe Billing',
      'Standard Support',
    ],
  },
  {
    id: 'professional',
    planId: WL.professional.id,
    label: 'Professional License',
    price: fmtUsd(WL.professional.amount_cents),
    currency: 'USD',
    period: WL.professional.period,
    duration: WL.professional.duration,
    highlight: true,
    bestValue: false,
    tagline: 'Most Popular',
    cta: 'Launch Professional',
    bestFor: [
      'Growing businesses',
      'Trading communities',
      'Financial education companies',
      'Commercial launches',
    ],
    includes: [
      'Everything in Starter',
      ...WL_BENEFITS.items,
      'Priority Deployment',
      'Priority Technical Support',
      'Business Consultation',
      'Faster Go Live',
      'Extended Licensing',
    ],
  },
  {
    id: 'enterprise',
    planId: WL.enterprise.id,
    label: 'Enterprise License',
    price: fmtUsd(WL.enterprise.amount_cents),
    currency: 'USD',
    period: WL.enterprise.period,
    duration: WL.enterprise.duration,
    highlight: false,
    bestValue: true,
    tagline: 'Best Value',
    cta: 'Launch Enterprise',
    bestFor: [
      'Investment firms',
      'Global businesses',
      'Financial institutions',
      'Large organisations',
    ],
    includes: [
      'Everything in Professional',
      ...WL_BENEFITS.items,
      'Enterprise-grade licensing',
      'Dedicated Success Manager',
      'Strategic Platform Planning',
      'Priority Roadmap Access',
      'Long-Term Business Partnership',
    ],
  },
]

// ── Commercial Philosophy ─────────────────────────────────────────
export const PHILOSOPHY_PILLARS = [
  { id: 'ai',        label: 'AI Platform',                iconKey: 'Bot',            tone: 'orange' },
  { id: 'trading',   label: 'Trading Infrastructure',      iconKey: 'LineChart',      tone: 'green'  },
  { id: 'wl',        label: 'White-Label',                 iconKey: 'Building2',      tone: 'cyan'   },
  { id: 'academy',   label: 'Academy',                     iconKey: 'GraduationCap',  tone: 'purple' },
  { id: 'crm',       label: 'CRM',                          iconKey: 'UsersRound',     tone: 'blue'   },
  { id: 'analytics', label: 'Analytics',                    iconKey: 'PieChart',       tone: 'blue'   },
  { id: 'billing',   label: 'Billing',                      iconKey: 'CreditCard',     tone: 'green'  },
  { id: 'admin',     label: 'Administration',               iconKey: 'Cog',            tone: 'orange' },
  { id: 'updates',   label: 'Continuous Improvements',      iconKey: 'RefreshCw',      tone: 'purple' },
]

// ── License Comparison Matrix ────────────────────────────────────
// truthy value in each column = capability present.
export const COMPARISON = [
  { id: 'ai',      capability: 'AI Platform',                  starter: true, professional: true, enterprise: true },
  { id: 'trade',   capability: 'Trading Platform',              starter: true, professional: true, enterprise: true },
  { id: 'wl',      capability: 'White-Label',                   starter: true, professional: true, enterprise: true },
  { id: 'academy', capability: 'Trading Academy',                starter: true, professional: true, enterprise: true },
  { id: 'crm',     capability: 'CRM',                             starter: true, professional: true, enterprise: true },
  { id: 'anal',    capability: 'Analytics',                       starter: true, professional: true, enterprise: true },
  { id: 'bill',    capability: 'Stripe Billing',                  starter: true, professional: true, enterprise: true },
  { id: 'domain',  capability: 'Custom Domain',                   starter: true, professional: true, enterprise: true },
  { id: 'brand',   capability: 'Branding',                        starter: true, professional: true, enterprise: true },
  { id: 'upd',     capability: 'Platform Updates',                starter: true, professional: true, enterprise: true },
  { id: 'depl',    capability: 'Priority Deployment',             starter: false, professional: true, enterprise: true },
  { id: 'supp',    capability: 'Priority Support',                starter: false, professional: true, enterprise: true },
  { id: 'csm',     capability: 'Dedicated Success Manager',       starter: false, professional: false, enterprise: true },
]

// ── Everything Included (by category) ───────────────────────────────
export const INCLUDED = [
  {
    id: 'ai', label: 'AI',    iconKey: 'Bot',           tone: 'orange',
    modules: [
      { label: 'AI Mentor',      iconKey: 'MessageSquare' },
      { label: 'Knowledge Base',  iconKey: 'BookOpen'      },
      { label: 'Prompt Engine',    iconKey: 'Wand2'        },
    ],
  },
  {
    id: 'trading', label: 'Trading', iconKey: 'LineChart', tone: 'green',
    modules: [
      { label: 'Terminal',         iconKey: 'Zap'          },
      { label: 'Charts',            iconKey: 'BarChart3'    },
      { label: 'Strategy Builder',  iconKey: 'Wand2'        },
      { label: 'Backtesting',       iconKey: 'FlaskConical' },
      { label: 'Scanner',           iconKey: 'ScanLine'     },
      { label: 'Portfolio',         iconKey: 'Wallet'       },
    ],
  },
  {
    id: 'business', label: 'Business', iconKey: 'Briefcase', tone: 'blue',
    modules: [
      { label: 'CRM',              iconKey: 'UsersRound'   },
      { label: 'Billing',           iconKey: 'CreditCard'   },
      { label: 'Analytics',          iconKey: 'PieChart'     },
      { label: 'Notifications',      iconKey: 'BellRing'     },
    ],
  },
  {
    id: 'wl', label: 'White-Label', iconKey: 'Building2', tone: 'cyan',
    modules: [
      { label: 'Branding',         iconKey: 'Palette'      },
      { label: 'Domain',            iconKey: 'Globe'        },
      { label: 'Workspace',          iconKey: 'ServerCog'    },
      { label: 'Administration',     iconKey: 'Cog'          },
    ],
  },
  {
    id: 'learning', label: 'Learning', iconKey: 'GraduationCap', tone: 'purple',
    modules: [
      { label: 'Academy',          iconKey: 'BookOpen'     },
      { label: 'Progress',           iconKey: 'TrendingUp'  },
      { label: 'Certification Ready', iconKey: 'BadgeCheck' },
    ],
  },
]

// ── ROI – systems consolidated by Kalki Ai Cloud ───────────────────────────
export const ROI_SYSTEMS = [
  { id: 'crm',       label: 'CRM',                     iconKey: 'UsersRound',   tone: 'blue'   },
  { id: 'trading',   label: 'Trading Platform',         iconKey: 'LineChart',    tone: 'green'  },
  { id: 'academy',   label: 'Academy',                  iconKey: 'GraduationCap',tone: 'purple' },
  { id: 'billing',   label: 'Billing',                  iconKey: 'CreditCard',   tone: 'green'  },
  { id: 'analytics', label: 'Analytics',                iconKey: 'PieChart',     tone: 'blue'   },
  { id: 'ai',        label: 'AI',                        iconKey: 'Bot',          tone: 'orange' },
  { id: 'customer',  label: 'Customer Management',       iconKey: 'ContactRound', tone: 'orange' },
  { id: 'admin',     label: 'Administration',            iconKey: 'Cog',          tone: 'cyan'   },
]

// ── Platform Launch Timeline (8 steps) ───────────────────────────────
export const LAUNCH_TIMELINE = [
  { id: 'purchase',  label: 'Purchase License',      iconKey: 'ShoppingCart', tone: 'orange', summary: 'Select the licence tier that matches your business plan.',        body: 'Pick Starter / Professional / Enterprise from the pricing cards — upgrade any time.' },
  { id: 'pay',       label: 'Stripe Payment',         iconKey: 'CreditCard',   tone: 'green',  summary: 'PCI-DSS-compliant checkout with tax invoice.',                       body: 'Cards, GPay, ApplePay and Netbanking accepted. Instant tax invoice + email receipt.' },
  { id: 'ws',        label: 'Workspace Created',      iconKey: 'ServerCog',    tone: 'blue',   summary: 'Dedicated multi-tenant workspace provisioned automatically.',       body: 'Isolated database, storage, cache and background workers. ~90 seconds end-to-end.' },
  { id: 'brand',     label: 'Brand Configuration',    iconKey: 'Palette',      tone: 'purple', summary: 'Logo, colours, typography, email templates — all tokenised per tenant.', body: 'Nine brand surfaces configured through the admin console. Rollback in one click.' },
  { id: 'domain',    label: 'Domain Setup',           iconKey: 'Globe',        tone: 'cyan',   summary: 'Point DNS — SSL, HSTS and CDN provisioned automatically.',          body: 'Bring your own domain (or subdomain). Multi-domain routing supported for global desks.' },
  { id: 'modules',   label: 'Module Activation',      iconKey: 'ToggleRight',  tone: 'orange', summary: 'Toggle AI, Terminal, Academy, Billing and more per tenant.',        body: 'Modules toggle per tenant + per role. Billing prorates automatically for metered capabilities.' },
  { id: 'team',      label: 'Team Invitation',        iconKey: 'UsersRound',   tone: 'green',  summary: 'Invite by email, SSO or bulk CSV — role assignment built-in.',       body: 'RBAC groups mapped to SSO. Seat allocation, activation and deactivation from the console.' },
  { id: 'live',      label: 'Go Live',                 iconKey: 'Rocket',       tone: 'orange', summary: 'One click flips the tenant public — your customers land on your brand.', body: 'Zero downtime cut-over. Support and success teams monitor the first hours in production.' },
]

// ── Stripe Payment Experience (8 steps) ─────────────────────────────
export const PAYMENT_STEPS = [
  { id: 'select',   label: 'License Selected',       iconKey: 'MousePointerClick', tone: 'orange' },
  { id: 'stripe',   label: 'Secure Stripe Checkout',  iconKey: 'Lock',              tone: 'blue'   },
  { id: 'confirm',  label: 'Payment Confirmation',    iconKey: 'CheckCircle2',      tone: 'green'  },
  { id: 'invoice',  label: 'Invoice Generated',       iconKey: 'FileText',          tone: 'purple' },
  { id: 'activate', label: 'License Activated',       iconKey: 'BadgeCheck',        tone: 'orange' },
  { id: 'ws',       label: 'Workspace Created',       iconKey: 'ServerCog',         tone: 'cyan'   },
  { id: 'welcome',  label: 'Welcome Email',            iconKey: 'MailPlus',          tone: 'green'  },
  { id: 'ready',    label: 'Dashboard Ready',          iconKey: 'LayoutDashboard',   tone: 'orange' },
]

// ── Licensing Terms ──────────────────────────────────────────────
export const TERMS = [
  { id: 'one',      label: 'One subscription = one workspace',    body: 'Each active licence provisions and maintains a single fully-branded tenant workspace.', iconKey: 'ServerCog',    tone: 'blue'   },
  { id: 'duration', label: 'Duration matches plan',                body: 'Quarterly is 3 months. Half-Yearly is 6 months. Annual is 12 months. Renewals extend without disruption.', iconKey: 'CalendarRange', tone: 'orange' },
  { id: 'updates',  label: 'Platform updates included',            body: 'Every improvement we ship reaches your tenant automatically while the subscription is active.', iconKey: 'RefreshCw',    tone: 'green'  },
  { id: 'renew',    label: 'Renewals preserve everything',         body: 'Renewing extends access without touching your data, branding, users or configuration.', iconKey: 'Repeat',       tone: 'purple' },
  { id: 'upgrade',  label: 'Upgrades any time',                    body: 'Move up (Starter → Professional → Enterprise) any time with prorated invoicing.', iconKey: 'ArrowUpRight', tone: 'orange' },
  { id: 'tax',      label: 'Taxes handled at checkout',             body: 'Applicable local taxes and charges are calculated by Stripe during checkout where required.', iconKey: 'FileText',     tone: 'cyan'   },
]

// ── Enterprise Support Tiers ──────────────────────────────────────
export const SUPPORT_TIERS = [
  {
    id: 'starter', label: 'Starter', accent: 'orange', matches: 'Starter licence',
    tagline: 'Standard technical support',
    items: [
      'Email + ticket support',
      'Business-hours response SLA',
      'Community + docs access',
    ],
  },
  {
    id: 'pro', label: 'Professional', accent: 'orange', matches: 'Professional licence', highlight: true,
    tagline: 'Priority + consultation',
    items: [
      'Priority ticket lane',
      'Business consultation calls',
      'Faster response SLA',
      'Priority deployment queue',
    ],
  },
  {
    id: 'ent', label: 'Enterprise', accent: 'green', matches: 'Enterprise licence',
    tagline: 'Dedicated success + strategy',
    items: [
      'Dedicated success manager',
      'Strategic platform planning',
      'Priority roadmap access',
      'Long-term partnership',
    ],
  },
]

// ── FAQ ───────────────────────────────────────────────────────
export const FAQ = [
  { q: 'What is included in the license?',                a: 'Every licence — Starter, Professional and Enterprise — includes the complete Kalki Ai Cloud platform: AI Platform, Trading Terminal, Strategy Builder, Backtesting, Scanner, Portfolio, Academy, Analytics, Billing, CRM, Notifications and the White-Label admin console. Higher tiers add priority service levels, dedicated success and strategic partnership.' },
  { q: 'Can I upgrade later?',                             a: 'Yes. Move from Starter → Professional → Enterprise any time. Stripe calculates the prorated difference at checkout so you only pay for the remaining term.' },
  { q: 'Can I renew early?',                                a: 'Absolutely. Renewals extend your current term without interrupting your workspace, users, branding, data or integrations. Renewing before expiry protects continuity of pricing.' },
  { q: 'Can I migrate my existing branding?',               a: 'Yes — during onboarding we import your logo, colour palette, typography, email templates and favicon. If you already run a platform, we assist with a shadow-launch practice run before your Go-Live day.' },
  { q: 'Can I connect my own domain?',                      a: 'Every plan supports custom domains. Point your DNS records and Kalki Ai Cloud provisions an auto-renewing SSL certificate, HSTS, and CDN edge caching. Multiple domains and subdomains are supported.' },
  { q: 'How does Stripe payment work?',                    a: 'Selecting a licence redirects to a secure Stripe Checkout page. Cards, GPay, ApplePay and Netbanking are supported. On successful payment, an invoice is generated, the licence is activated, and your workspace is provisioned automatically.' },
  { q: 'How are updates delivered?',                        a: 'Continuously. Every platform improvement rolls out to your tenant with zero downtime. No upgrade projects. No maintenance windows. No customer-side integration work.' },
  { q: 'What happens if my subscription expires?',          a: 'Your workspace enters a grace period during which access is preserved. Renewing restores full access. If the grace period lapses, your data is retained for 30 days for a smooth reactivation.' },
  { q: 'Can my team access the platform?',                  a: 'Yes. Every licence supports multi-user access. Invite by email, SSO or bulk CSV. Role-based permissions apply per module and per book. Seat allocation is unlimited within your licence.' },
]
